'use client'

import { Collapse, Divider, Flex, Grid, GridCol, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import Plus from '@/components/Icons/SelfServe/Plus'
import Minus from '@/components/Icons/SelfServe/Minus'
import { isMobile } from 'react-device-detect'
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
import { useContactSupport } from '@/hooks/useContactSupport'

interface FrequentlyAskedQuestionsProps {
    category?: string
}

interface FAQBlock {
    question: string
    answer: string
    onClick: () => void
    collapseIn: boolean
}

function FAQAnswer({ answer }: { answer: string }) {
    const { contactUsLabel } = useContactSupport({ defaultLabel: answer })
    return (
        <Text c={'var(--GM-Text-Black, #181818)'} fz={16} fw={400} lh={'150%'}>
            {contactUsLabel}
        </Text>
    )
}

export const FrequentlyAskedQuestions = ({ category }: FrequentlyAskedQuestionsProps) => {
    const [faq1Opened, { toggle: toggleFaq1 }] = useDisclosure(false)
    const [faq2Opened, { toggle: toggleFaq2 }] = useDisclosure(false)
    const [faq3Opened, { toggle: toggleFaq3 }] = useDisclosure(false)
    const [faq4Opened, { toggle: toggleFaq4 }] = useDisclosure(false)
    const [faq5Opened, { toggle: toggleFaq5 }] = useDisclosure(false)
    const isLabPanels = category === 'lab'
    const flags = useFlags()
    const ldClient = useLDClient()

    const standardFAQs: FAQBlock[] = [
        {
            question: 'What is General Medicine?',
            answer:
                'General Medicine is a healthcare store designed to get you the care you need, whenever and wherever you need it. ' +
                "From same-day virtual visits to specialist consultations and in-person appointments, we're here to provide high-quality, " +
                'personalized care. With upfront pricing and no subscription or membership fees, General Medicine makes healthcare accessible ' +
                'and hassle-free.',
            onClick: toggleFaq1,
            collapseIn: faq1Opened,
        },
        {
            question: 'What can General Medicine help with?',
            answer:
                'General Medicine provides convenient care for all of your of health needs, including common and chronic conditions, ' +
                "specialist consultations, and preventative care. We make it easy to get the care you need—whether it's getting the right " +
                'prescriptions, labs or imaging, or connecting you with the right specialists or in-person providers—ensuring your healthcare ' +
                'experience is seamless and stress-free.',
            onClick: toggleFaq2,
            collapseIn: faq2Opened,
        },
        {
            question: 'How much does General Medicine cost?',
            answer:
                "You'll pay per visit for any video or chat visits you book with our team. Most major insurance plans are accepted, but " +
                "insurance is not required. Prices vary based on the type of care you need, and we'll always provide up front prices with and " +
                'without insurance before you book—so there are no surprises.',
            onClick: toggleFaq3,
            collapseIn: faq3Opened,
        },
        {
            question: 'Who is the care team at General Medicine?',
            answer:
                'Our care team is made up of board certified medical professionals, including doctors and care navigation experts, all committed ' +
                "to helping you navigate your health. With decades of experience, we're here to make your health journey as smooth and " +
                'stress-free as possible',
            onClick: toggleFaq4,
            collapseIn: faq4Opened,
        },
    ]

    const labFAQs: FAQBlock[] = [
        {
            question: 'Who is eligible to use this service?',
            answer:
                'Our lab testing services are designed for adults aged 18 and older. For those ' +
                'under 18, we recommend consulting a pediatric healthcare provider. If ' +
                "you're assisting someone, they'll need to sign up on their own.",
            onClick: toggleFaq1,
            collapseIn: faq1Opened,
        },
        {
            question: 'Where do I go for testing?',
            answer:
                'We make it easy to book at certified labs near you, including Quest Diagnostics, ' +
                'Labcorp and Bio-Reference. Availability varies by location. Simply choose your ' +
                "preferred location, and you'll receive a booking confirmation with all the " +
                "details. If you'd prefer another lab, we can help send your lab orders " +
                'there instead.',
            onClick: toggleFaq2,
            collapseIn: faq2Opened,
        },
        {
            question: 'Is insurance accepted?',
            answer:
                'Insurance is accepted for some lab services. See the pricing section on each ' +
                'lab page for details. You can compare prices and select your billing method when booking. ' +
                'We also accept most major insurance plans for follow-up visits with our care team, and ' +
                "we'll always provide clear, upfront pricing before you book.",
            onClick: toggleFaq3,
            collapseIn: faq3Opened,
        },
        {
            question: 'Can I consult with a clinician about my results?',
            answer:
                'Yes! You can text us for basic questions about your results. ' +
                "If you need further care, treatment, or additional testing, you'll be able " +
                'to book a virtual visit for personalized support.',
            onClick: toggleFaq4,
            collapseIn: faq4Opened,
        },
    ]

    return (
        <Stack gap={24}>
            {renderHeader()}
            {!isMobile && <Divider bd={'1px solid var(--gm-foreground-fg-outline-primary)'} />}

            <Grid>
                <GridCol span={{ md: 4, lg: 4 }} visibleFrom="md" />
                <GridCol span={{ base: 12, md: 8, lg: 8 }}>
                    <Stack gap={20}>
                        {(isLabPanels ? labFAQs : standardFAQs).map((faq) => {
                            return (
                                <Stack gap={20} key={faq.question}>
                                    {renderFAQBlock(faq)}
                                    {<Divider size="md" />}
                                </Stack>
                            )
                        })}
                        {renderPrivacyNotice()}
                    </Stack>
                </GridCol>
            </Grid>
        </Stack>
    )

    function renderHeader() {
        return (
            <Text
                c={isMobile ? 'var(--Foreground-fg-primary, #1F1E1D)' : 'var(--GM-Text-Black, #181818)'}
                fz={isMobile ? 36 : 48}
                fw={isMobile ? 'var(--gm-weight-display-small, 450)' : 400}
                lh={isMobile ? '130%' : '125%'}
                lts={isMobile ? '-0.24px' : '-1.28px'}
            >
                Frequently asked questions
            </Text>
        )
    }

    function renderFAQBlock(faq: FAQBlock) {
        return (
            <Stack gap="xl">
                <Flex justify="space-between" align="center" onClick={faq.onClick} style={{ cursor: 'pointer' }}>
                    <Text
                        c={'var(--GM-Text-Black, #181818)'}
                        fz={isMobile ? 16 : 24}
                        fw={400}
                        lh={isMobile ? '150%' : '130%'}
                    >
                        {faq.question}
                    </Text>
                    {faq.collapseIn ? <Minus /> : <Plus />}
                </Flex>
                <Collapse in={faq.collapseIn} mb="lg">
                    <FAQAnswer answer={faq.answer} />
                </Collapse>
            </Stack>
        )
    }

    function renderPrivacyNotice() {
        return (
            <Stack gap="xl">
                <Flex justify="space-between" align="center" onClick={toggleFaq5} style={{ cursor: 'pointer' }}>
                    <Text
                        c={'var(--GM-Text-Black, #181818)'}
                        fz={isMobile ? 16 : 24}
                        fw={400}
                        lh={isMobile ? '150%' : '130%'}
                    >
                        How do you protect my health information?
                    </Text>
                    <Plus />
                </Flex>
                <Collapse in={faq5Opened} mb="lg">
                    <Text c={'var(--GM-Text-Black, #181818)'} fz={16} fw={400} lh={'150%'}>
                        We take your privacy seriously and secure your data with the highest standards. Your personal
                        health information is never shared or sold for advertising or marketing purposes—ensuring your
                        data stays private and protected. For more details, you can review our&nbsp;
                        <Text
                            data-dd-privacy="allow"
                            component="a"
                            target="_blank"
                            href="https://generalmedicine.co/terms-of-service/"
                            fz={16}
                            fw={400}
                            c="blue.6"
                            ml={2}
                            mr={4}
                        >
                            Terms of Service
                        </Text>
                        ,&nbsp;
                        <Text
                            data-dd-privacy="allow"
                            component="a"
                            target="_blank"
                            href="https://generalmedicine.co/privacy-policy/"
                            fz={16}
                            fw={400}
                            c="blue.6"
                            ml={2}
                            mr={4}
                        >
                            Privacy Policy
                        </Text>
                        , and&nbsp;
                        <Text
                            data-dd-privacy="allow"
                            component="a"
                            target="_blank"
                            href="https://generalmedicine.co/hipaa-privacy-practices"
                            fw={400}
                            fz={16}
                            ml={4}
                            c="blue.6"
                        >
                            HIPAA Notice of Privacy Practices
                        </Text>
                        .
                    </Text>
                </Collapse>
            </Stack>
        )
    }
}
